import {
  useState,
  useEffect,
  forwardRef,
  useMemo,
  useImperativeHandle,
  useRef,
} from 'react';
import {Trans, useTranslation} from 'react-i18next';
import {ButtonProps} from 'components/common/Button/Button';
import ModalSheet from 'components/common/ModalSheet';
import StandardModalSheet from 'components/common/StandardModalSheet';
import {useModalControls} from 'hooks';
import {closeSession} from 'components/sessions/utils';

const LIMIT = 60;

export type SessionCloseModalRef = {
  open: () => void;
};

type Props = {
  reservationId: string;
};
const SessionCloseModal = forwardRef<SessionCloseModalRef, Props>(
  ({reservationId}, ref) => {
    const {t} = useTranslation();
    const timer = useRef<ReturnType<typeof setInterval>>();
    const {isOpen, closeModal, openModal} = useModalControls();
    const [count, setCount] = useState(LIMIT);

    const discardChangeButtons: ButtonProps[] = useMemo(() => {
      const primaryButton = {
        label: t('continue_session'),
        primary: true,
        onClick: closeModal,
      };
      const secondaryButton: ButtonProps = {
        link: true,
        label: t('end_session_now'),
        onClick: () => closeSession(reservationId),
      };
      return [primaryButton, secondaryButton];
    }, [t, closeModal, reservationId]);

    useEffect(() => {
      if (isOpen) {
        setCount(LIMIT);
        timer.current = setInterval(() => setCount(c => c - 1), 1000);
      }
      return () => clearInterval(timer.current);
    }, [isOpen]);

    useEffect(() => {
      if (count <= 0) {
        clearInterval(timer.current);
        closeSession(reservationId);
      }
    }, [count, closeModal, reservationId]);

    useImperativeHandle(
      ref,
      () => {
        return {
          open: openModal,
        };
      },
      [openModal],
    );

    return (
      <ModalSheet
        isOpenModalSheet={isOpen}
        handleCloseModal={closeModal}
        title={t('session_close_modal.title')}
        closeOnDocumentClick={false}
        closeButtonType="click"
      >
        <StandardModalSheet
          buttons={discardChangeButtons}
          text={
            <Trans
              i18nKey="session_close_modal.text"
              values={{
                countdown: count,
              }}
            />
          }
        />
      </ModalSheet>
    );
  },
);

export {SessionCloseModal};
